const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const boundsToRect = (bounds) => ({
  x: Number(bounds?.minX || 0),
  y: Number(bounds?.minY || 0),
  width: Math.max(1, Number(bounds?.width || 0)),
  height: Math.max(1, Number(bounds?.height || 0)),
});

const buildTextLayer = ({
  bounds,
  textSource,
  colorRole,
  fontPx,
  templateFamily,
  templatePart,
}) => {
  if (!bounds) return null;
  const rect = boundsToRect(bounds);
  const height = Math.max(1, rect.height);
  const baselineRatio = clamp(fontPx / height, 0.25, 0.95);
  return {
    layerKind: 'text',
    textSource,
    type: 'rect',
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    rotation: 0,
    strokeWidth: 3,
    cornerRadius: 0,
    skewX: 0,
    fill: false,
    stroke: false,
    visible: true,
    mainTextVisible: false,
    subTextVisible: false,
    colorRole,
    fillColorMode: 'theme',
    fillColorRole: colorRole,
    fillColorValue: '#ffffff',
    strokeColorMode: 'theme',
    strokeColorRole: colorRole,
    strokeColorValue: '#ffffff',
    textPaddingX: 0,
    textFontRatio: clamp(fontPx / height, 0.2, 1.5),
    textBaselineRatio: baselineRatio,
    templateFamily,
    templatePart,
  };
};

const buildShapeLayer = ({
  type,
  rect,
  colorRole,
  cornerRadius = 0,
  skewX = 0,
  templateFamily,
  templatePart,
}) => ({
  layerKind: 'shape',
  type,
  x: rect.x,
  y: rect.y,
  width: Math.max(1, rect.width),
  height: Math.max(1, rect.height),
  rotation: 0,
  strokeWidth: 3,
  cornerRadius,
  skewX,
  fill: true,
  stroke: false,
  visible: true,
  mainTextVisible: false,
  subTextVisible: false,
  colorRole,
  fillColorMode: 'theme',
  fillColorRole: colorRole,
  fillColorValue: '#ffffff',
  strokeColorMode: 'theme',
  strokeColorRole: colorRole,
  strokeColorValue: '#ffffff',
  templateFamily,
  templatePart,
});

const buildImageLayer = ({
  rect,
  imageData,
  cropMode = 'original',
  templateFamily,
  templatePart,
}) => ({
  layerKind: 'image',
  type: 'image',
  x: rect.x,
  y: rect.y,
  width: Math.max(1, rect.width),
  height: Math.max(1, rect.height),
  rotation: 0,
  strokeWidth: 3,
  cornerRadius: 0,
  skewX: 0,
  fill: false,
  stroke: false,
  visible: true,
  mainTextVisible: false,
  subTextVisible: false,
  colorRole: 'primary',
  fillColorMode: 'theme',
  fillColorRole: 'primary',
  fillColorValue: '#ffffff',
  strokeColorMode: 'theme',
  strokeColorRole: 'primary',
  strokeColorValue: '#ffffff',
  imageData: typeof imageData === 'string' ? imageData : '/preset-logo-photo-icon.svg',
  imageCropMode: cropMode,
  resetX: rect.x,
  resetY: rect.y,
  resetWidth: Math.max(1, rect.width),
  resetHeight: Math.max(1, rect.height),
  resetRotation: 0,
  templateFamily,
  templatePart,
});

export const buildTemplateLayerBlueprint = ({ preset, metrics }) => {
  if (!metrics) return null;
  const family = preset || 'classic-slide';
  const templateBounds = metrics.templateShapeBounds;
  if (!templateBounds) return null;

  const layers = [];

  if (family === 'minimal-box') {
    const minimal = metrics.minimal || {};
    const nameRect = {
      x: 0,
      y: 0,
      width: Math.max(minimal.nameWidth || templateBounds.width, 1),
      height: Math.max(minimal.nameHeight || templateBounds.height / 2, 1),
    };
    const titleRect = {
      x: 0,
      y: nameRect.height,
      width: Math.max(minimal.titleWidth || templateBounds.width, 1),
      height: Math.max(minimal.titleHeight || Math.max(1, templateBounds.height - nameRect.height), 1),
    };
    layers.push(
      buildShapeLayer({ type: 'rect', rect: nameRect, colorRole: 'primary', templateFamily: family, templatePart: 'body-main' }),
      buildShapeLayer({ type: 'rect', rect: titleRect, colorRole: 'secondary', templateFamily: family, templatePart: 'body-sub' }),
    );
  } else if (family === 'edge-link') {
    const edgeLink = metrics.edgeLink || {};
    const barRect = {
      x: Number(edgeLink.barX || 0),
      y: 0,
      width: Math.max(1, Number(edgeLink.barWidth || metrics.baseBoxWidth || templateBounds.width)),
      height: Math.max(1, Number(edgeLink.bodyHeight || metrics.baseBoxHeight || templateBounds.height)),
    };
    const accentRect = {
      x: Number(edgeLink.accentX || barRect.x),
      y: 0,
      width: Math.max(1, Number(edgeLink.accentWidth || 14)),
      height: barRect.height,
    };
    const tipRect = {
      x: Number(edgeLink.tipX || Math.max(barRect.x, barRect.x + barRect.width - 40)),
      y: 0,
      width: Math.max(1, Number(edgeLink.tipWidth || 60)),
      height: barRect.height,
    };
    const tipSkewX = Number(edgeLink.tipSkewX || -36);
    layers.push(
      buildShapeLayer({
        type: 'rect',
        rect: barRect,
        colorRole: 'secondary',
        templateFamily: family,
        templatePart: 'body-bar',
      }),
      buildShapeLayer({
        rect: accentRect,
        type: 'rect',
        colorRole: 'primary',
        templateFamily: family,
        templatePart: 'accent',
      }),
      buildShapeLayer({
        type: 'parallelogram',
        rect: tipRect,
        colorRole: 'secondary',
        skewX: tipSkewX,
        templateFamily: family,
        templatePart: 'body-tip',
      }),
    );
  } else if (family === 'logo-badge') {
    const logoBadge = metrics.logoBadge || {};
    const bodyRect = {
      x: Number(logoBadge.bodyX || 0),
      y: 0,
      width: Math.max(1, Number(logoBadge.bodyWidth || metrics.baseBoxWidth || templateBounds.width)),
      height: Math.max(1, Number(logoBadge.bodyHeight || metrics.baseBoxHeight || templateBounds.height)),
    };
    const accentRect = {
      x: Number(logoBadge.accentX || bodyRect.x),
      y: Number(logoBadge.accentY || 0),
      width: Math.max(1, Number(logoBadge.accentWidth || 12)),
      height: Math.max(1, Number(logoBadge.accentHeight || bodyRect.height)),
    };
    const logoPlateRect = {
      x: Number(logoBadge.logoPlateX || -72),
      y: Number(logoBadge.logoPlateY || 0),
      width: Math.max(1, Number(logoBadge.logoPlateSize || 120)),
      height: Math.max(1, Number(logoBadge.logoPlateSize || 120)),
    };
    const logoImageRect = {
      x: Number(logoBadge.logoImageX || logoPlateRect.x + 18),
      y: Number(logoBadge.logoImageY || logoPlateRect.y + 18),
      width: Math.max(1, Number(logoBadge.logoImageWidth || Math.max(1, logoPlateRect.width - 36))),
      height: Math.max(1, Number(logoBadge.logoImageHeight || Math.max(1, logoPlateRect.height - 36))),
    };
    layers.push(
      buildShapeLayer({
        type: 'rect',
        rect: bodyRect,
        colorRole: 'secondary',
        templateFamily: family,
        templatePart: 'body',
      }),
      buildShapeLayer({
        type: 'roundedRect',
        rect: accentRect,
        colorRole: 'primary',
        cornerRadius: Math.max(0, Number(logoBadge.accentRadius || 6)),
        templateFamily: family,
        templatePart: 'accent',
      }),
      buildShapeLayer({
        type: 'rect',
        rect: logoPlateRect,
        colorRole: 'secondary',
        templateFamily: family,
        templatePart: 'logo-plate',
      }),
      buildImageLayer({
        rect: logoImageRect,
        imageData: '/preset-logo-photo-icon.svg',
        cropMode: 'original',
        templateFamily: family,
        templatePart: 'logo-icon',
      }),
    );
  } else if (family === 'modern-accent') {
    const modern = metrics.modern || {};
    const skewX = -Math.max(0, Number(modern.skewFactor || 0)) * Math.max(1, Number(metrics.baseBoxHeight || templateBounds.height));
    const height = Math.max(1, Number(metrics.baseBoxHeight || templateBounds.height));
    const bodyRect = {
      x: Number(modern.skewOffset || 0),
      y: 0,
      width: Math.max(1, Number(metrics.baseBoxWidth || templateBounds.width)),
      height,
    };
    const accentRect = {
      x: Number(modern.accentOffset || 0),
      y: 0,
      width: Math.max(1, Number(modern.accentWidth || 0)),
      height,
    };
    layers.push(
      buildShapeLayer({ type: 'parallelogram', rect: bodyRect, colorRole: 'secondary', skewX, templateFamily: family, templatePart: 'body' }),
      buildShapeLayer({ type: 'parallelogram', rect: accentRect, colorRole: 'primary', skewX, templateFamily: family, templatePart: 'accent' }),
    );
  } else {
    const classic = metrics.classic || {};
    const bodyRect = {
      x: 0,
      y: 0,
      width: Math.max(1, Number(metrics.baseBoxWidth || templateBounds.width)),
      height: Math.max(1, Number(metrics.baseBoxHeight || templateBounds.height)),
    };
    const accentRect = {
      x: 0,
      y: 0,
      width: Math.max(1, Number(classic.accentWidth || 0)),
      height: bodyRect.height,
    };
    layers.push(
      buildShapeLayer({
        type: 'roundedRect',
        rect: bodyRect,
        colorRole: 'secondary',
        cornerRadius: Math.max(0, Number(classic.radius || 0)),
        templateFamily: family,
        templatePart: 'body',
      }),
      buildShapeLayer({
        type: 'roundedRect',
        rect: accentRect,
        colorRole: 'primary',
        cornerRadius: Math.max(0, Number(classic.radius || 0)),
        templateFamily: family,
        templatePart: 'accent',
      }),
    );
  }

  const titleLayer = buildTextLayer({
    bounds: metrics.textBounds?.main,
    textSource: 'main',
    colorRole: 'textUpper',
    fontPx: Number(metrics.mainFontPx || 36),
    templateFamily: family,
    templatePart: 'title-text',
  });
  const subLayer = buildTextLayer({
    bounds: metrics.textBounds?.sub,
    textSource: 'sub',
    colorRole: 'textLower',
    fontPx: Number(metrics.subFontPx || 24),
    templateFamily: family,
    templatePart: 'sub-title-text',
  });
  if (titleLayer) layers.push(titleLayer);
  if (subLayer) layers.push(subLayer);

  return { family, layers: layers.filter(Boolean) };
};

export default buildTemplateLayerBlueprint;
